import { Component, EventEmitter, forwardRef, Input, Output } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { AbstractValueAccessor } from '../abstract-value-accessor';
import { SharedModule } from '../shared.module';

export const PERCENTAGE_INPUT_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => PercentageInputComponent),
    multi: true,
};

@Component({
    selector: 'lf-percentage',
    styleUrls: ['./percentage-input.scss'],
    templateUrl: './percentage-input.component.html',
    providers: [
        PERCENTAGE_INPUT_VALUE_ACCESSOR,
        DecimalPipe,
    ],
    standalone: true,
    imports: [
        SharedModule,
    ],
})
export class PercentageInputComponent extends AbstractValueAccessor<number>
{
    public displayAmount: string = '';
    public error: boolean = false;

    @Input()
    public min: number;
    @Input()
    public max: number;
    @Input()
    public placeholder: string = '0.00';
    @Input()
    public readonly: boolean = false;
    @Input()
    public required: boolean = false;
    @Input()
    public showSymbol: boolean = false;

    @Output()
    public blurred: EventEmitter<any> = new EventEmitter<any>();
    @Output()
    public inputValueChanged: EventEmitter<any> = new EventEmitter<any>();

    private _numberPipe: DecimalPipe;

    constructor(_numberPipe: DecimalPipe) {
        super();

        this._numberPipe = _numberPipe;
    }

    public writeValue(value: number) {
        value = this.constrainAmount(value);

        if (value !== this._value) {
            this._value = value;
            this.displayAmount = this.formatDisplayCurrency(this.value);

            // Control value accessor - makes ngModel validation work.
            this.propagateChange(this._value);
        }
    }

    /**
     * Converts the provided display amount (in dollars for USD) to an integer based unit of measurement (cents for USD).
     * @param {number} actualAmount
     * @returns {number}
     */
    public formatDisplayCurrency(actualAmount: number): string {
        if (typeof actualAmount === 'undefined' || actualAmount === null) {
            return '';
        }

        return this._numberPipe.transform(actualAmount / 100, '.2');
    }

    /**
     * Sets the control value to the provided display amount (in dollars for USD)
     * to an integer based unit of measurement (cents for USD).
     * @param {string} displayAmount
     */
    public setValueFromDisplay(displayAmount: string) {
        displayAmount = displayAmount.replace(/,/g, '');

        // If they supplied a value, and then deleted all int's...
        if (!displayAmount) {
            this.displayAmount = '0.00';
            this.writeValue(0);
        }

        // If we get something stupid like 2.2222, round it off to a cent format.
        const overlargeDecimalLengthRegex = /^[-]?[0-9]*(\.[0-9]{3,})?$/;
        if (displayAmount && overlargeDecimalLengthRegex.test(displayAmount)) {
            displayAmount = this.formatDisplayCurrency(this.normalizeDisplayAmountToInteger(Number(displayAmount)));
            this.displayAmount = displayAmount;
        }

        // This presumes an American currency formatting. We should revisit this upon supporting additional currencies.
        // Optional negation operator, 0-n digits prior to decimal.
        // If there is a decimal component, require the decimal character and 1-2 more digits.
        const dollarRegex = /^[-]?[0-9]*(\.[0-9]{1,2})?$/;
        if (displayAmount && dollarRegex.test(displayAmount)) {
            const value = this.normalizeDisplayAmountToInteger(Number(displayAmount));
            this.writeValue(value);
        }
    }

    /**
     * Converts the provided display amount (in dollars for USD) to an integer based unit of measurement (cents for USD).
     * @param {number} displayAmount
     * @returns {number}
     */
    public normalizeDisplayAmountToInteger(displayAmount: number) {
        return Math.round(displayAmount * 100);
    }

    /**
     * Force the provided amount to be constrained to the min and max inputs, if set.
     * @param {number} amount
     * @returns {number}
     */
    public constrainAmount(amount: number) {
        if (typeof amount !== 'undefined' && amount !== null) {
            if (this.min !== null && this.min > amount) {
                amount = this.min;
            } else if (this.max !== null && this.max < amount) {
                amount = this.max;
            }
        }

        return amount;
    }
}
